/**
 * FileUploadModal — drag-and-drop file upload with company/contact linking.
 *
 * Upload flow:
 *   1. POST /api/documents/upload → get write SAS + read SAS
 *   2. PUT file bytes directly to blob via write SAS
 *   3. POST /api/documents → save file metadata with read SAS URL
 */
import { useState, useRef, useCallback } from "react";
import { Upload, X, File, Loader2, Check } from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { Button } from "./ui";
import { getDocumentUploadToken, saveDocument } from "../services/api";
import CompanyContactPicker from "./CompanyContactPicker";

export default function FileUploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [tags, setTags] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      setError("File must be under 50 MB");
      return;
    }
    setFile(f);
    setError(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  }, [handleFile]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);

    try {
      // 1. Get SAS token
      const { sasUrl, readSasUrl, blobName } = await getDocumentUploadToken(
        file.name,
        file.type || "application/octet-stream",
      );

      // 2. Upload directly to blob
      const uploadRes = await fetch(sasUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!uploadRes.ok) throw new Error("Blob upload failed");

      // 3. Save metadata
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      await saveDocument({
        type: "file",
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        blobName,
        sasUrl: readSasUrl,
        companyName,
        contactIds: contactEmail ? [contactEmail] : [],
        contactEmail,
        tags: tagList,
      });

      setSuccess(true);
      setTimeout(() => {
        onUploaded?.();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: 12,
          width: 480,
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <h2 style={{ fontFamily: fonts.heading, fontSize: 18, margin: 0 }}>Upload File</h2>
          <X size={20} color={colors.textSecondary} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>

        <div style={{ padding: "20px 24px" }}>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? colors.accent : colors.border}`,
              borderRadius: 8,
              padding: 32,
              textAlign: "center",
              cursor: "pointer",
              background: dragOver ? colors.accentDim : "transparent",
              transition: "all 0.15s",
              marginBottom: 16,
            }}
          >
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: "none" }}
            />
            {file ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <File size={20} color={colors.accent} />
                <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{file.name}</span>
                <span style={{ fontSize: 12, color: colors.textSecondary }}>
                  ({(file.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              </div>
            ) : (
              <>
                <Upload size={28} color={colors.textSecondary} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 14, color: colors.textSecondary, marginBottom: 4 }}>
                  Drop a file here, or click to browse
                </div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  PDF, Word, Excel, PowerPoint, images (max 50 MB)
                </div>
              </>
            )}
          </div>

          {/* Company/Contact picker */}
          <CompanyContactPicker
            companyName={companyName}
            contactEmail={contactEmail}
            onCompanyChange={setCompanyName}
            onContactChange={setContactEmail}
          />

          {/* Tags */}
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, display: "block" }}>
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. annual-report, q1-2026"
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: 13,
                fontFamily: fonts.body,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                background: colors.bg,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Error/Success */}
          {error && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, background: "rgba(239,68,68,0.1)", color: colors.danger, fontSize: 12 }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 6, background: "rgba(16,185,129,0.1)", color: colors.success, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <Check size={14} /> File uploaded successfully
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "16px 24px",
          borderTop: `1px solid ${colors.border}`,
        }}>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleUpload} disabled={!file || uploading || success}>
            {uploading ? (
              <><Loader2 size={14} className="spin" /> Uploading...</>
            ) : (
              <><Upload size={14} /> Upload</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
